import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { SubmitHandler, useForm } from "react-hook-form";

import IconHeart from "@/icons/icon-heart";
import Button from "~/components/common/button";
import EVAErrors from "~/components/form/eva-errors";
import InputField from "~/components/form/input-field";
import { wishlistNameFields, wishlistNameFieldSchema } from "~/shared/wishlist/wishlist.model";
import { wishlistNameMaxlength, wishlistNameMinlength } from "~/shared/wishlist/zod-schemas.class";

import Dialog from "../../common/dialog";
import Icon from "../../common/icon";
import createWishlistDialogStyles from "./create-wishlist-dialog.module.scss";
import styles from "./wishlist.module.scss";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  submit: SubmitHandler<wishlistNameFields>;
  error?: any;
  cleanError?: () => void;
};

export default function CreateWishlistDialog({ isOpen, onClose, cleanError, submit, error }: Props) {
  const t = useTranslations();

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<wishlistNameFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(wishlistNameFieldSchema),
  });

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <Icon icon={IconHeart} />
          <span>{t("generic.myaccount.wishlist.create_cta")}</span>
        </>
      }
      onClose={onClose}
      onAfterOpen={cleanError}
      onAfterClose={() => setValue("wishlistName", "")}
      customStyles={createWishlistDialogStyles}
    >
      <form onSubmit={handleSubmit(submit)} className={styles.form}>
        <InputField
          {...register("wishlistName")}
          type="text"
          label={t("generic.myaccount.wishlist.field")}
          error={
            error?.Error?.Type === "Wishlist:NameMustBeUnique"
              ? t(`error.${error?.Error?.Type}`)
              : errors.wishlistName?.message
              ? t(errors.wishlistName?.message, {
                  fieldName: t("generic.myaccount.wishlist.field"),
                  minLength: wishlistNameMinlength,
                  maxLength: wishlistNameMaxlength,
                })
              : undefined
          }
          clearValue={() => {
            cleanError?.();
            setValue("wishlistName", "");
            clearErrors("wishlistName");
          }}
        />
        {error?.Error?.Type != "Wishlist:NameMustBeUnique" && <EVAErrors error={error} />}
        <Button className={styles.submitModal} variant="secondary" type="submit">
          {t("generic.save")}
        </Button>
      </form>
    </Dialog>
  );
}
